import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { CSSObject, styled, Theme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAppDispatch, useAppSelector } from "store";
import { KOZYCLANLOGOWHITE } from "assets";
import { MENU } from "./utils";
import {
  Box,
  Divider,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Backdrop,
  Fade,
  SwipeableDrawer,
} from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import LogoutIcon from "@mui/icons-material/Logout";
import LogoutModal from "../shared/modal/LogoutModal";
import { getUserData } from "../../utils/storage";
import ChangePasswordModal from "../shared/modal/ChangePasswordModa";
import { Cancel, HelpOutlineOutlined, Password } from "@mui/icons-material";

// Dynamic drawer width calculation based on screen size
const getDrawerWidth = (theme: Theme) => {
  if (theme.breakpoints.values.xl <= window.innerWidth) {
    return 350;
  } else if (theme.breakpoints.values.lg <= window.innerWidth) {
    return 330;
  } else if (theme.breakpoints.values.md <= window.innerWidth) {
    return 320;
  } else {
    return 280;
  }
};

// Enhanced drawer styles with smoother transitions
const openedMixin = (theme: Theme): CSSObject => ({
  width: getDrawerWidth(theme),
  transition: theme.transitions.create(["width", "box-shadow"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  boxShadow: "4px 0px 10px rgba(0, 0, 0, 0.15)",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create(["width", "box-shadow"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  boxShadow: "2px 0px 5px rgba(0, 0, 0, 0.1)",
});

// Styled Drawer with improved responsive behavior - for desktop only
const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: open ? getDrawerWidth(theme) : `calc(${theme.spacing(8)} + 1px)`,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    backgroundColor: "#08253c",
    color: "#fff",
    border: "none",
    ...(open ? openedMixin(theme) : closedMixin(theme)),
  },
}));

export default function MiniDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { open, userDetails } = useAppSelector(
    (state) => state.appLayout
  ) as any;
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [selectedMenu, setSelectedMenu] = useState<string>("");
  const [userType, setUserType] = useState<string>("guest");
  const [isMenuClosing, setIsMenuClosing] = useState(false);

  // Define menu visibility based on user type (memoized)
  const allowedMenus = useMemo(() => ({
    host: ["1", "4"],
    merchant: ["2", "3", "5", "6", "7"],
  }), []);

  // Filter menu based on user type (memoized)
  const filteredMenu = useMemo(() => 
    MENU.filter((menu) => allowedMenus[userType as keyof typeof allowedMenus]?.includes(menu.id)),
  [MENU, allowedMenus, userType]);

  useEffect(() => {
    const { userType } = getUserData() || { userType: "guest" };
    setUserType(userType);

    const findUrl = MENU.find((menu) =>
      menu.items.some((subItem) => subItem.url === location.pathname)
    );
    // Set default menu based on user type
    if (userType === "merchant") {
      setSelectedMenu(findUrl?.id || "2");
    } else if (userType === "host") {
      setSelectedMenu(findUrl?.id || "1");
    } else {
      setSelectedMenu(MENU[0]?.id || "");
    }
  }, [location.pathname]);

  // Smooth drawer toggle with animation
  const handleDrawerToggle = () => {
    if (open) {
      setIsMenuClosing(true);
      setTimeout(() => {
        dispatch({ type: "appLayout/toggleSidebar" });
        setIsMenuClosing(false);
      }, 100);
    } else {
      dispatch({ type: "appLayout/toggleSidebar" });
    }
  };

  const handleMenuSelection = (menuId: string) => {
    // Navigate to the first item of the selected menu directly
    // The useEffect hook will update selectedMenu based on the location change
    const menu = MENU.find((menu) => menu.id === menuId);
    if (menu && menu.items.length > 0) {
      navigate(menu.items[0].url);
    }
  };

  const handleNavigation = (url: string) => navigate(url);

  // Find selected content (memoized)
  const selectedContent = useMemo(() => 
    MENU.find((menu) => menu.id === selectedMenu),
  [selectedMenu, MENU]);

  // Helper function to determine if a menu item is completed
  const isItemCompleted = (itemId: string) => {
    if (!userDetails) return false;
    if (itemId === "vendorAgreement") {
      return userDetails.vendorAgreement?.executionStatus === "ACCEPTED";
    }
    return Object.keys(userDetails).includes(itemId);
  };

  // Helper to determine if an item is rejected
  const isItemRejected = (itemId: string) => {
    if (!userDetails) return false;
    if (itemId === "vendorAgreement") {
      return userDetails.vendorAgreement?.executionStatus === "REJECTED";
    }
    return false;
  };

  // Dynamic styles for better touch experience on mobile
  const getItemButtonStyles = (isActive: boolean) => ({
    minHeight: { xs: 48, sm: 54, md: 60 },
    width: "100%",
    px: 2,
    py: { xs: 0.75, sm: 1 },
    fontFamily: "Maven Pro, sans-serif",
    borderRadius: "4px",
    mx: 1,
    my: { xs: 0.25, sm: 0.5 },
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    bgcolor: isActive ? "rgba(255,255,255,0.15)" : "transparent",
    "&:hover": {
      bgcolor: "rgba(255,255,255,0.2)",
      transform: "translateX(4px)",
    },
    "&:active": {
      bgcolor: "rgba(255,255,255,0.25)",
      transform: "translateX(2px) scale(0.98)",
    },
  });

  // Create the drawer content component to be reused in both mobile and desktop views
  const DrawerContent = () => (
    <>
      {/* Drawer Header with improved responsive styling */}
      <Box
        sx={{
          bgcolor: "rgba(0,0,0,0.3)",
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          padding: { xs: 1, sm: 1.5 },
          height: { xs: 56, sm: 64 },
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            color: "#fff",
            transition: "transform 0.3s ease, background-color 0.2s ease",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
              transform: "scale(1.05)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
          aria-label={open ? "Close sidebar" : "Open sidebar"}
        >
          {open ? <MenuIcon sx={{ ml: 0.5 }} /> : <ChevronRightIcon />}
        </IconButton>
        
        {open && (
          <Fade in={open} timeout={300}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                marginLeft: 2,
                maxWidth: "calc(100% - 48px)",
              }}
            >
              <img
                src={KOZYCLANLOGOWHITE}
                alt="Kozyclan Logo"
                style={{
                  height: isTablet || isMobile ? "28px" : "32px",
                  marginBottom: "4px",
                  transition: "height 0.3s ease",
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: 11, sm: 12, md: 14, lg: 15 },
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                  opacity: isMenuClosing ? 0 : 1,
                  transition: "opacity 0.3s ease",
                }}
              >
                {userType?.toUpperCase()} ID: {userDetails?.searchIdKey}{userDetails?.searchId}
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      <Box sx={{ display: "flex", height: "calc(100% - 64px)" }}>
        {/* Side Icons Menu with improved hover effects and touch feedback */}
        <Box
          sx={{
            width: open ? { xs: 60, sm: 65 } : "100%",
            bgcolor: "#03121F",
            display: "flex",
            flexDirection: "column",
            py: 1,
            boxShadow: "inset -1px 0 3px rgba(0,0,0,0.2)",
          }}
        >
          {filteredMenu.map((menu) => (
            <Tooltip 
              key={menu.id} 
              title={!open ? menu.title || "" : ""} 
              placement="right"
              arrow
              enterDelay={500}
            >
              <ListItemIcon
                sx={{
                  display: "flex",
                  minHeight: { xs: 48, sm: 56, md: 65 },
                  minWidth: { xs: 48, sm: 56, md: 65 },
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  color: selectedMenu === menu.id ? "#4CAF50" : "#fff",
                  borderLeft:
                    selectedMenu === menu.id
                      ? "3px solid #4CAF50"
                      : "3px solid transparent",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.15)",
                  },
                  "&:active": {
                    transform: "translateY(0px) scale(0.95)",
                  },
                }}
                onClick={() => handleMenuSelection(menu.id)}
                aria-label={menu.title || "Menu item"}
              >
                {React.cloneElement(menu.icon as any, { 
                  fontSize: isMobile ? "small" : "medium",
                  style: { 
                    transition: "transform 0.2s ease", 
                    transform: selectedMenu === menu.id ? "scale(1.1)" : "scale(1)" 
                  }
                })}
              </ListItemIcon>
            </Tooltip>
          ))}

          {/* Bottom utility icons with improved spacing, tooltips and touch feedback */}
          <Box
            sx={{
              mt: "auto",
              mb: 2,
              display: "flex",
              flexDirection: "column",
              gap: { xs: 0.5, sm: 1 },
            }}
          >
            <Tooltip title="Support" placement="right" arrow>
              <ListItemIcon
                aria-label="support"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "#fff",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "#90CAF9",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
                onClick={() => navigate("/support")}
              >
                <HelpOutlineOutlined fontSize={isMobile ? "small" : "medium"} />
              </ListItemIcon>
            </Tooltip>

            <Tooltip title="Change Password" placement="right" arrow>
              <ListItemIcon
                aria-label="change password"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "#fff",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "#FFD54F",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
                onClick={() => setIsChangePasswordModalOpen(true)}
              >
                <Password fontSize={isMobile ? "small" : "medium"} />
              </ListItemIcon>
            </Tooltip>

            <Tooltip title="Logout" placement="right" arrow>
              <ListItemIcon
                aria-label="logout"
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "#fff",
                  transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.15)",
                    color: "#EF5350",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
                onClick={() => setIsLogoutModalOpen(true)}
              >
                <LogoutIcon fontSize={isMobile ? "small" : "medium"} />
              </ListItemIcon>
            </Tooltip>
          </Box>
        </Box>

        {/* Expanded Menu Items with improved styling, animations and responsiveness */}
        {open && selectedContent && (
          <Fade in={open && !isMenuClosing} timeout={300}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255,255,255,0.3) transparent",
                "&::-webkit-scrollbar": {
                  width: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255,255,255,0.3)",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.5)",
                  },
                },
              }}
            >
              <List sx={{ width: "100%", py: { xs: 0.5, sm: 1 } }}>
                {selectedContent.items.map((subItem) => {
                  const isCompleted = isItemCompleted(subItem.id);
                  const isRejected = isItemRejected(subItem.id);
                  const isActive = location.pathname === subItem.url;

                  return (
                    <React.Fragment key={subItem.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigation(subItem.url)}
                          sx={getItemButtonStyles(isActive)}
                        >
                          <ListItemText
                            primary={subItem.name}
                            primaryTypographyProps={{
                              fontSize: { xs: 12, sm: 13, md: 14 },
                              fontWeight: isActive ? 600 : 400,
                              color: isActive
                                ? "rgba(255,255,255,0.95)"
                                : "rgba(255,255,255,0.7)",
                              sx:{
                                transition: "all 0.2s ease",
                              }
                            }}
                          />
                          {isCompleted ? (
                            <Tooltip title="Completed" placement="left" arrow>
                              <CheckCircleIcon
                                sx={{
                                  color: "#4CAF50",
                                  marginLeft: "10px",
                                  fontSize: { xs: 16, sm: 18, md: 20 },
                                  transition: "transform 0.2s ease",
                                  "&:hover": { transform: "scale(1.2)" },
                                }}
                              />
                            </Tooltip>
                          ) : (
                            isRejected && (
                              <Tooltip title="Rejected" placement="left" arrow>
                                <Cancel
                                  sx={{
                                    color: "#FF5252",
                                    marginLeft: "10px",
                                    fontSize: { xs: 16, sm: 18, md: 20 },
                                    transition: "transform 0.2s ease",
                                    "&:hover": { transform: "scale(1.2)" },
                                  }}
                                />
                              </Tooltip>
                            )
                          )}
                        </ListItemButton>
                      </ListItem>
                      <Divider
                        sx={{
                          bgcolor: "rgba(255,255,255,0.1)",
                          width: "85%",
                          mx: "auto",
                          my: 0.25,
                        }}
                      />
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          </Fade>
        )}
      </Box>
    </>
  );

  return (
    <>
      {/* Use different drawer components based on screen size */}
      {isMobile ? (
        // Mobile view - SwipeableDrawer for better mobile UX
        <SwipeableDrawer
          anchor="left"
          open={open}
          onClose={handleDrawerToggle}
          onOpen={() => dispatch({ type: "appLayout/toggleSidebar" })}
          disableBackdropTransition={!isTablet}
          disableDiscovery={isTablet}
          PaperProps={{
            sx: {
              width: getDrawerWidth(theme),
              backgroundColor: "#08253c",
              color: "#fff",
              border: "none",
              boxShadow: "4px 0px 10px rgba(0, 0, 0, 0.15)",
            },
          }}
          ModalProps={{
            keepMounted: true, 
          }}
          swipeAreaWidth={20}
          hysteresis={0.3}
          minFlingVelocity={500}
        >
          <DrawerContent />
        </SwipeableDrawer>
      ) : (
        // Desktop view - Persistent drawer
        <Drawer variant="permanent" open={open}>
          <DrawerContent />
        </Drawer>
      )}
      
      {/* Backdrop for mobile view when sidebar is open */}
      {isMobile && (
        <Backdrop
          sx={{ 
            color: '#fff', 
            zIndex: (theme) => theme.zIndex.drawer - 1,
            backgroundColor: 'rgba(0,0,0,0.5)' 
          }}
          open={open}
          onClick={handleDrawerToggle}
        />
      )}
      
      {/* Fixed mini toggle button for mobile - always visible */}
      {isMobile && !open && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            left: 10,
            top: 10,
            zIndex: 1200,
            backgroundColor: '#08253c',
            color: '#fff',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: '#0A3152',
            },
            width: 40,
            height: 40,
          }}
          aria-label="Open menu"
        >
          <MenuIcon />
        </IconButton>
      )}
      
      {/* Modal components */}
      {isLogoutModalOpen && (
        <LogoutModal
          open={isLogoutModalOpen}
          onClose={() => setIsLogoutModalOpen(false)}
        />
      )}
      {isChangePasswordModalOpen && (
        <ChangePasswordModal
          open={isChangePasswordModalOpen}
          onClose={() => setIsChangePasswordModalOpen(false)}
        />
      )}
    </>
  );
}